import {Component, computed, ElementRef, inject, input, LOCALE_ID, Signal, viewChild} from '@angular/core';
import {formatDate} from '@angular/common';

import {BaseChartDirective} from 'ng2-charts';
import {Chart, ChartConfiguration, ChartData} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';


import {DataBatchTools, Formatter} from '@selpia/shared/utils';
import {DataPoint, Measure} from '@selpia/shared/models';
import {Range} from '../l12m-bar-chart/l12m-bar-chart.component';

@Component({
  selector: 'lib-l12m-line-chart',
  imports: [BaseChartDirective],
  templateUrl: './l12m-line-chart.component.html',
  styleUrl: './l12m-line-chart.component.scss',
})
export class L12mLineChartComponent {
  readonly #locale = inject(LOCALE_ID);

  canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  readonly dataPoints = input.required<DataPoint[]>();
  readonly measure = input.required<Measure | undefined>();
  readonly period = input.required<string | undefined>();
  readonly date = input.required<Date | undefined>();
  readonly range = input<Range>();
  readonly isHighlighted = input<boolean>();

  constructor() {
    Chart.defaults.font.family = 'Poppins';
  }

  protected readonly chartData: Signal<ChartData<'line'>> = computed(() => {
    this.isHighlighted();
    const horizon = [...Array(12)
      .keys()];
    const focusDate = this.date();
    const dataPoints = this.dataPoints();

    const measure = this.measure();

    const numberFont = this.#getCSS('--number-font');
    const lineColor = this.#getCSS('--chart-line-color');
    const highlightColor = this.#getCSS('--chart-line-highlight') || lineColor;
    const bgColor = this.#getCSS('--tile-background');

    const labels = new Array<string>();
    const points = new Array<number | null>();

    const data: ChartData<'line'> = {
      labels,
      datasets: [{
        data: points,
        borderWidth: 1,
        borderColor: lineColor,
        pointRadius({dataIndex}) {
          if (dataIndex === 0 || dataIndex === horizon.at(-1)) {
            return 2;
          }
          return 0;
        },
        pointHitRadius: 4,
        pointBackgroundColor({dataIndex}) {
          if (dataIndex === horizon.at(-1)) {
            return highlightColor;
          }
          return bgColor;
        },
        pointBorderColor({dataIndex}) {
          if (dataIndex === horizon.at(-1)) {
            return highlightColor;
          }
          return lineColor;
        },
        datalabels: {
          offset: 2,
          align: function ({dataIndex}) {
            return (dataIndex === horizon.at(-1)) ? 'right' : 'left';
          },
          formatter: (value: number): string => Formatter.formatByMeasure(value, measure, true),
          display: function ({dataIndex}): boolean {
            return (dataIndex === 0 || dataIndex === horizon.at(-1));
          },
          color: function ({dataIndex}) {
            return (dataIndex === horizon.at(-1)) ? highlightColor : lineColor;
          },
          font: {
            family: numberFont,
            size: 12,
          },
        },
      }],
    };

    if (!focusDate || !dataPoints) {
      return data;
    }

    const date = new Date(focusDate);

    horizon.forEach(() => {
      const point = DataBatchTools.getDataPointFor(dataPoints, {
        measureCode: this.measure()?.code,
        periodCode: this.period(),
        date,
      });

      points.unshift(point?.value || null);

      labels.unshift(formatDate(date, "MMM’YY", this.#locale));

      date.setDate(0);
    });


    return data;
  });

  protected readonly chartOptions: Signal<ChartConfiguration<'line'>['options']> = computed(() => {
    this.isHighlighted();
    const range = this.range();
    const measure = this.measure();

    return {
      maintainAspectRatio: false,
      responsive: true,
      layout: {
        padding: {
          right: 60,
          left: 60,
          top: 12,
          bottom: 12,
        },
      },
      scales: {
        x: {display: false},
        y: {
          suggestedMin: range?.min,
          suggestedMax: range?.max,
          display: false,
          bounds: 'data',
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          displayColors: false,
          cornerRadius: 2,
          bodyAlign: 'center',
          callbacks: {
            label: function (context) {
              const value = context.raw as number;
              return Formatter.formatByMeasure(value, measure, true);
            },
          },
        },
      },
    }
      ;
  });

  #getCSS(variableName: string): string {
    const canvas = this.canvas();
    if (!canvas) {
      return '';
    }

    return getComputedStyle(canvas.nativeElement)
      .getPropertyValue(variableName) || getComputedStyle(document.body)
      .getPropertyValue(variableName);
  }

  protected readonly plugins = [ChartDataLabels];
}
